<template>
  <div class="slide slide-leaderboard">
    <!-- Header -->
    <div class="slide-header">
      <h2 class="slide-title">{{ data.title }}</h2>
      <div v-if="data.subtitle" class="slide-subtitle">{{ data.subtitle }}</div>
    </div>

    <!-- Leaderboard -->
    <div class="leaderboard">
      <div
        v-for="(leader, index) in data.leaders"
        :key="index"
        class="leader-row"
        :style="{ animationDelay: `${index * 0.1}s` }"
      >
        <!-- Rank Badge -->
        <div class="rank-badge" :class="getRankClass(index)">
          {{ index + 1 }}
        </div>

        <!-- Avatar -->
        <div class="leader-avatar">
          <img
            v-if="leader.profilepic"
            :src="leader.profilepic"
            :alt="leader.name"
            class="avatar-image"
            @error="handleImageError($event)"
          />
          <div v-else class="avatar-initials" :class="getAvatarColor(index)">
            {{ leader.initials }}
          </div>
        </div>

        <!-- Info -->
        <div class="leader-info">
          <div class="leader-name">{{ leader.name }}</div>
          <div class="leader-meta">
            <span class="leader-team">{{ leader.team }}</span>
            <span
              v-for="(value, vIdx) in leader.topValues"
              :key="vIdx"
              class="leader-value"
              :class="getValueColor(value)"
            >{{ formatValue(value) }}</span>
          </div>
        </div>

        <!-- Scores -->
        <div class="leader-scores">
          <div
            v-for="(score, scoreIndex) in leader.scores"
            :key="scoreIndex"
            class="score-item"
          >
            <div class="score-value">{{ formatScore(score.value) }}</div>
            <div class="score-label">{{ score.label }}</div>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-if="!data.leaders || data.leaders.length === 0" class="empty-state">
        <div class="empty-icon">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none">
            <path d="M12 15C15.866 15 19 11.866 19 8C19 4.13401 15.866 1 12 1C8.13401 1 5 4.13401 5 8C5 11.866 8.13401 15 12 15Z" stroke="currentColor" stroke-width="1.5"/>
            <path d="M8.21 13.89L7 23L12 20L17 23L15.79 13.88" stroke="currentColor" stroke-width="1.5"/>
          </svg>
        </div>
        <div class="empty-text">No data available for this period</div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'LeaderboardSlide',
  props: {
    data: {
      type: Object,
      required: true
    },
    slideId: {
      type: String,
      default: ''
    }
  },
  methods: {
    getRankClass(index) {
      if (index === 0) return 'rank-gold';
      if (index === 1) return 'rank-silver';
      if (index === 2) return 'rank-bronze';
      return 'rank-default';
    },
    getAvatarColor(index) {
      const colors = ['avatar-violet', 'avatar-emerald', 'avatar-amber', 'avatar-sky', 'avatar-rose'];
      return colors[index % colors.length];
    },
    formatScore(value) {
      if (typeof value === 'number') {
        // If it's an integer, display without decimals
        if (Number.isInteger(value)) {
          return value.toString();
        }
        // Otherwise display with 1 decimal place
        return value.toFixed(1);
      }
      return value;
    },
    handleImageError(event) {
      event.target.style.display = 'none';
      event.target.parentElement.innerHTML = '<div class="avatar-initials avatar-violet">?</div>';
    },
    getValueColor(value) {
      // Hash the value string to get a consistent color for each value
      const colors = ['value-violet', 'value-emerald', 'value-amber', 'value-sky', 'value-rose'];
      if (!value) return colors[0];
      let hash = 0;
      const str = String(value).toLowerCase();
      for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
      }
      return colors[Math.abs(hash) % colors.length];
    },
    formatValue(value) {
      // Ensure single # prefix (values may already include #)
      if (!value) return '';
      const str = String(value);
      return str.startsWith('#') ? str : '#' + str;
    }
  }
};
</script>

<style lang="scss" scoped>
@import '../../../../assets/quarterly-slides.scss';

.slide-leaderboard {
  @extend .slide-base;
  background: $paper;
  padding: 80px;
  display: flex;
  flex-direction: column;
}

.slide-header {
  margin-bottom: 48px;
}

.slide-title {
  @extend .serif-headline;
  font-size: 56px;
  color: $ink;
  margin: 0;
}

.slide-subtitle {
  font-family: $font-body;
  font-size: 20px;
  color: $ink-muted;
  margin-top: 12px;
}

.leaderboard {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 24px;
  max-width: 1600px;
  margin: 0 auto;
  width: 100%;
}

.leader-row {
  display: flex;
  align-items: center;
  gap: 32px;
  background: #fff;
  border-radius: 24px;
  padding: 24px 40px;
  box-shadow: $shadow-soft;
  animation: slideInRow 0.5s $ease-out-quart backwards;
  transition: all 0.3s ease;

  &:hover {
    transform: translateX(8px);
    box-shadow: $shadow-medium;
  }
}

.rank-badge {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: $font-body;
  font-size: 24px;
  font-weight: 700;
  flex-shrink: 0;

  &.rank-gold {
    background: linear-gradient(135deg, #ffd700 0%, #ffb700 100%);
    color: #7a5c00;
    box-shadow: 0 4px 12px rgba(255, 183, 0, 0.4);
  }

  &.rank-silver {
    background: linear-gradient(135deg, #e8e8e8 0%, #c4c4c4 100%);
    color: #5a5a5a;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  &.rank-bronze {
    background: linear-gradient(135deg, #cd7f32 0%, #a56027 100%);
    color: #fff;
    box-shadow: 0 4px 12px rgba(205, 127, 50, 0.4);
  }

  &.rank-default {
    background: $paper-cool;
    color: $ink-muted;
  }
}

.leader-avatar {
  width: 72px;
  height: 72px;
  flex-shrink: 0;
}

.avatar-image {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
}

.avatar-initials {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: $font-body;
  font-size: 28px;
  font-weight: 600;
  color: #fff;

  &.avatar-violet { background: $violet-500; }
  &.avatar-emerald { background: $emerald; }
  &.avatar-amber { background: $amber; }
  &.avatar-sky { background: $sky; }
  &.avatar-rose { background: $rose; }
}

.leader-info {
  flex: 1;
  min-width: 0;
}

.leader-name {
  font-family: $font-body;
  font-size: 28px;
  font-weight: 600;
  color: $ink;
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.leader-meta {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.leader-team {
  font-family: $font-body;
  font-size: 18px;
  color: $ink-muted;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.leader-value {
  font-family: $font-body;
  font-size: 16px;
  font-weight: 600;
  padding: 6px 14px;
  border-radius: 100px;
  white-space: nowrap;

  &.value-violet {
    color: $violet-600;
    background: rgba($violet-500, 0.12);
  }

  &.value-emerald {
    color: darken($emerald, 10%);
    background: rgba($emerald, 0.12);
  }

  &.value-amber {
    color: darken($amber, 15%);
    background: rgba($amber, 0.15);
  }

  &.value-sky {
    color: darken($sky, 15%);
    background: rgba($sky, 0.15);
  }

  &.value-rose {
    color: darken($rose, 5%);
    background: rgba($rose, 0.12);
  }
}

.leader-scores {
  display: flex;
  gap: 32px;
}

.score-item {
  text-align: center;
  min-width: 100px;
}

.score-value {
  font-family: $font-body;
  font-size: 36px;
  font-weight: 700;
  color: $violet-600;
  line-height: 1;
  margin-bottom: 8px;
}

.score-label {
  font-family: $font-body;
  font-size: 14px;
  color: $ink-muted;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px;
  color: $ink-subtle;
}

.empty-icon {
  margin-bottom: 24px;
  opacity: 0.5;
}

.empty-text {
  font-family: $font-body;
  font-size: 20px;
}

@keyframes slideInRow {
  from {
    opacity: 0;
    transform: translateX(-40px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}
</style>
